import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  resetPassword: async (username: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', { username, new_password: newPassword });
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  listUsers: async () => {
    const response = await api.get('/auth/users');
    return response.data;
  },
  createUser: async (username: string, password: string, email?: string) => {
    const response = await api.post('/auth/register', { username, password, email });
    return response.data;
  },
  deleteUser: async (username: string) => {
    const response = await api.delete(`/auth/users/${username}`);
    return response.data;
  },
  toggleUserActive: async (username: string) => {
    const response = await api.put(`/auth/users/${username}/toggle-active`);
    return response.data;
  },
  resetUserPassword: async (username: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', { username, new_password: newPassword });
    return response.data;
  },
};

// Jobs API
export const jobsAPI = {
  createPriceAnalysis: async (formData: FormData) => {
    const response = await api.post('/jobs/analyze-price', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  createPropertyAnalysis: async (formData: FormData) => {
    const response = await api.post('/jobs/analyze-property', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  list: async (status?: string, limit = 50, offset = 0) => {
    const response = await api.get('/jobs', { params: { status, limit, offset } });
    return response.data;
  },
  listByType: async (jobType?: string, status?: string, limit = 50, offset = 0, search?: string) => {
    const params: Record<string, any> = { limit, offset };
    if (jobType) params.job_type = jobType;
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await api.get('/jobs', { params });
    return response.data;
  },
  get: async (jobId: string) => {
    const response = await api.get(`/jobs/${encodeURIComponent(jobId)}`);
    return response.data;
  },
  download: async (jobId: string) => {
    const response = await api.get(`/jobs/${encodeURIComponent(jobId)}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
  delete: async (jobId: string) => {
    const response = await api.delete(`/jobs/${encodeURIComponent(jobId)}`);
    return response.data;
  },
};

// Settings API
export const settingsAPI = {
  get: async () => {
    const response = await api.get('/settings');
    return response.data;
  },
  updateNDS: async (value: number) => {
    const response = await api.put('/settings/nds', { value });
    return response.data;
  },
  updateDeflator: async (value: number) => {
    const response = await api.put('/settings/deflator', { value });
    return response.data;
  },
  updateApiKey: async (value: string) => {
    const response = await api.put('/settings/api-key', { value });
    return response.data;
  },
  deleteApiKey: async () => {
    const response = await api.delete('/settings/api-key');
    return response.data;
  },
};

// Monitoring API (admin only)
export const monitoringAPI = {
  getTasks: async (statusFilter?: string, jobType?: string, limit = 100, offset = 0) => {
    const params: Record<string, any> = { limit, offset };
    if (statusFilter) params.status_filter = statusFilter;
    if (jobType) params.job_type = jobType;
    const response = await api.get('/monitoring/tasks', { params });
    return response.data;
  },
  getWorkers: async () => {
    const response = await api.get('/monitoring/workers');
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/monitoring/stats');
    return response.data;
  },
  getCleanupHistory: async () => {
    const response = await api.get('/monitoring/cleanup-history');
    return response.data;
  },
  getTokenUsage: async (username?: string, jobType?: string, limit = 100, offset = 0) => {
    const params: Record<string, any> = { limit, offset };
    if (username) params.username = username;
    if (jobType) params.job_type = jobType;
    const response = await api.get('/monitoring/token-usage', { params });
    return response.data;
  },
  getTokenUsageDetail: async (jobId: string) => {
    const response = await api.get(`/monitoring/token-usage/${encodeURIComponent(jobId)}`);
    return response.data;
  },
};
